/**
 * Ciblage d'un passage de la page : quelles phrases chercher, et combien vaut
 * un texte trouvé.
 *
 * Extrait de `actions.ts` et `ui.ts` le 2026-09-15, **sans changement de
 * comportement** : première étape des garde-fous du guidage. Le parcours du DOM
 * reste dans `actions.ts` ; ce qui se calcule sur des chaînes vit ici, pour être
 * rejoué en test sans navigateur.
 */

/**
 * Les mots qui ne désignent pas un passage : interrogation, pronoms, liaisons,
 * consignes de guidage.
 *
 * Même liste que `apps/api/src/lib/mots-significatifs.ts` : le widget ne partage
 * aucun code avec l'API (il est chargé seul sur les sites des clients), d'où la
 * copie. Sans elle, le seul mot « votre » donnait 116 points à un texte sans
 * rapport — presque autant qu'une phrase entière retrouvée.
 */
const MOTS_VIDES = new Set([
  'quel', 'quels', 'quelle', 'quelles', 'que', 'quoi', 'qui', 'comment', 'combien', 'pourquoi', 'quand',
  'les', 'des', 'une', 'ces', 'cet', 'cette', 'votre', 'vos', 'notre', 'nos', 'leur', 'leurs',
  'vous', 'nous', 'moi', 'toi', 'lui', 'eux', 'elle', 'elles', 'ils', 'celui', 'celle', 'ceux',
  'tout', 'tous', 'toute', 'toutes',
  'est', 'sont', 'suis', 'etes', 'êtes', 'etait', 'était', 'etre', 'être', 'avoir', 'avez', 'avons', 'ont',
  'peux', 'peut', 'pouvez', 'puis', 'fait', 'faire', 'faites', 'sait', 'savoir',
  'pour', 'avec', 'dans', 'sur', 'par', 'pas', 'sans', 'chez', 'entre', 'vers', 'mais', 'donc', 'car',
  'plus', 'moins', 'bien', 'aussi', 'ici',
  'trouve', 'trouver', 'montre', 'montrer', 'montrez', 'voir', 'passage', 'parle', 'page', 'section',
  'emmene', 'emmène', 'emmenez', 'amene', 'amène', 'amenez', 'aller', 'allez',
  'merci', 'bonjour',
]);

/** Tokens significatifs extraits d'une phrase (ex. « studio 107 »). */
export function phraseNeedles(phrase?: string): string[] {
  const raw = (phrase || '').replace(/\s+/g, ' ').trim();
  if (raw.length < 3) return [];
  const lower = raw.toLowerCase();
  const needles: string[] = [];
  // Phrase complète (tronquée) + préfixes décroissants.
  for (const len of [Math.min(60, lower.length), 40, 28, 18, 12]) {
    if (lower.length >= Math.min(len, 3)) needles.push(lower.slice(0, Math.min(len, lower.length)));
  }
  // Mots significatifs, et paires de mots voisins. Un mot SEUL n'est une aiguille
  // que si la phrase n'en contient qu'un : sinon, un mot commun à la phrase et à
  // un texte sans rapport suffisait à le désigner.
  const tokens = motsDeLaPhrase(lower);
  if (tokens.length === 1) needles.push(tokens[0]!);
  for (let i = 0; i + 1 < tokens.length; i++) {
    needles.push(`${tokens[i]} ${tokens[i + 1]}`);
  }
  // Déduplique en gardant l'ordre (plus précis d'abord).
  const seen = new Set<string>();
  return needles.filter((n) => {
    if (n.length < 3 || seen.has(n)) return false;
    seen.add(n);
    return true;
  });
}

/** Les mots de la phrase qui peuvent se retrouver épars dans un bloc. */
export function motsDeLaPhrase(phrase?: string): string[] {
  return (phrase || '')
    .toLowerCase()
    .split(/[^a-zàâäéèêëïîôùûüç0-9]+/i)
    .filter((t) => t.length >= 3 || /^\d+$/.test(t))
    .filter((t) => !MOTS_VIDES.has(t));
}

/**
 * Score d'un nœud de texte, avant l'ajustement selon la balise qui le porte
 * (première passe de `findElementByPhrase`). 0 = le texte ne correspond pas.
 */
export function scoreTexte(texte: string, needles: string[], mots: string[]): number {
  let score = 0;
  for (let i = 0; i < needles.length; i++) {
    if (texte.includes(needles[i]!)) {
      score = Math.max(score, 120 - i * 3 + Math.min(40, needles[i]!.length));
      break;
    }
  }
  // Mots non contigus : tous présents dans le même nœud / bloc parent.
  if (mots.length >= 2) {
    const hit = mots.filter((t) => texte.includes(t)).length;
    if (hit === mots.length) score = Math.max(score, 90 + hit * 8);
    else if (hit >= 2 && hit >= Math.ceil(mots.length * 0.6)) {
      score = Math.max(score, 50 + hit * 8);
    }
  } else if (mots.length === 1 && texte.includes(mots[0]!)) {
    score = Math.max(score, 70);
  }
  return score;
}

/** Une action telle que le widget la reçoit, réduite à ce qui sert au ciblage. */
export type ActionCiblage = { type?: string; phrase?: string };

/** Ce que le visiteur doit VOIR passe avant la zone où l'on fait défiler. */
function rangCiblage(a: ActionCiblage): number {
  return a.type === 'point' || a.type === 'highlight' ? 0 : 1;
}

/**
 * Phrases de ciblage : celles des actions — pointage et surlignage d'abord —,
 * puis les passages que la réponse cite entre guillemets ou en gras.
 *
 * Le pointage passe avant le défilement : « où est le numéro de téléphone ? »
 * faisait défiler jusqu'au bloc adresse puis pointait le numéro, et le widget,
 * qui suivait la première phrase venue, surlignait l'adresse (audit modelabs).
 *
 * Plus de phrase fabriquée avec les premiers mots de la réponse (« nous proposons
 * sites vitrines référencement ») : écrite nulle part sur la page, elle y était
 * pourtant cherchée — et trouvée n'importe où.
 */
export function collectGuidePhrases(actions: ActionCiblage[], reply: string): string[] {
  const out: string[] = [];
  // `sort` est stable : l'ordre du modèle est gardé à rang égal.
  for (const a of [...actions].sort((x, y) => rangCiblage(x) - rangCiblage(y))) {
    if (a.phrase && a.phrase.trim().length >= 3) out.push(a.phrase.trim());
  }
  // Extraits entre guillemets / gras dans la réponse (souvent le passage cité).
  const quoted = reply.match(/[«"]([^»"]{6,80})[»"]|\*\*([^*]{6,80})\*\*/g);
  if (quoted) {
    for (const q of quoted) {
      out.push(q.replace(/^[«"*]+|[»"*]+$/g, '').trim());
    }
  }
  return [...new Set(out.filter(Boolean))];
}

/**
 * Une annonce de geste : montrer, guider, emmener, pointer, « juste ici ».
 * Copie de celle de l'API (`lib/guidage.ts`) — le widget ne partage aucun code —,
 * élargie aux tournures qui ne sont vraies que si la page a bougé.
 */
const ANNONCE =
  /\bje (vais|peux) (aussi |également |directement )?vous (le |la |les |y |en )?(montrer|guider|emmener|conduire|diriger)|\bje vous (y |le |la |les )?(montre|emmène|guide|conduis|dirige)(?!\p{L})|\bje (pointe|surligne|mets en évidence|la place|le place)(?!\p{L})|\bjuste ici\b|\bvoilà,? je vous montre/iu;

/**
 * Retire du texte affiché les annonces d'un geste qui n'a pas eu lieu.
 *
 * Le dernier mot revient au widget : lui seul sait si la phrase visée a été
 * trouvée sur la page. L'API retire déjà les annonces sans action ; ici, ce sont
 * celles dont l'action a échoué — passage absent, masqué, recopié d'ailleurs.
 */
export function retirerAnnonces(texte: string): string {
  return texte
    .split('\n')
    .map((ligne) =>
      ligne
        .split(/(?<=[.!?…])\s+/)
        .filter((phrase) => !ANNONCE.test(phrase))
        .join(' ')
    )
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Ce que l'aperçu du Studio dit à la place d'un geste qu'il ne peut pas faire.
 *
 * Dans l'aperçu, le personnage vit dans la page du Studio et le site du client
 * dans un cadre d'une autre origine : le navigateur interdit d'y faire défiler
 * ou d'y surligner quoi que ce soit. Plutôt que de laisser croire à une panne —
 * ou de promettre « je vous montre » sans rien montrer —, on décrit le geste.
 *
 * `null` quand il n'y a rien à décrire : aucune action de pointage ni de défilement.
 */
export function noteApercu(actions: ActionCiblage[], reply: string): string | null {
  const gestes = actions.filter((a) => a.type === 'point' || a.type === 'highlight' || a.type === 'scroll');
  if (!gestes.length) return null;
  const phrase = collectGuidePhrases(gestes, reply)[0];
  const cible = phrase ? ` : « ${phrase.length > 90 ? `${phrase.slice(0, 89)}…` : phrase} »` : ' le passage concerné';
  return `Aperçu : je ne peux pas faire défiler votre site ici. Sur votre site, je vous montrerais${cible}.`;
}
